import { Avatar, Box, Button, Rating, Stack, Typography } from "@mui/material";
import { CheckCircle, HelpRounded, TurnedIn } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import LoginSignUpForm from "../OrderCheckOut/LoginSignUpForm";
import ApplyForm from "../OrderCheckOut/ApplyForm";
import DetailSection from "./DetailSection";
import { tokens } from "../../store/tokens";

const JobSidebar = ({
  isApplication,
  isLogged,
  isLogin,
  setIsLogin,
  loading,
  setLoading,
  setIsSubmitting,
  setIsLogged,
  setIsApplication,
  user,
  form,
  setForm,
  fetchJobDetails,
  isMember,
  handleSnackOpen,
  handleSnackClose,
  handleOpen,
}) => {
  const job = fetchJobDetails?.[0];
  const navigate = useNavigate();

  const userHasApplied =
    job?.poster !== user?.sopnoid &&
    Array.isArray(job?.userAppliState) &&
    job.userAppliState.some(
      (app) =>
        app.user_id === user?.sopnoid && app.app_refer && app.app_refer !== ""
    );

  const handleNavigation = () => {
    const agencyId = job?.c_refer || job?.poster;
    if (agencyId) {
      navigate(`/jobAgency/${agencyId}`);
    }
  };

  const paymentVerified = job?.user_state?.[0]?.pay_veri == 1;
  const posterVerified = job?.user_state?.[0]?.state === 1;

  if (isApplication) {
    return (
      <Box sx={{ padding: "16px" }}>
        {!isLogged ? (
          <LoginSignUpForm
            isLogin={isLogin}
            setIsLogin={setIsLogin}
            loading={loading}
            setLoading={setLoading}
            setIsSubmitting={setIsSubmitting}
            setIsLogged={setIsLogged}
            handleSnackOpen={handleSnackOpen}
            handleSnackClose={handleSnackClose}
            isLogged={isLogged}
          />
        ) : (
          <ApplyForm
            form={form}
            setForm={setForm}
            user={user}
            isMember={isMember}
            fetchJobDetails={job}
            isLogged={isLogged}
            handleSnackOpen={handleSnackOpen}
            handleSnackClose={handleSnackClose}
            setIsApplication={setIsApplication}
          />
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={1.5} sx={{ padding: "16px" }}>
        {!userHasApplied ? (
          <Button
            fullWidth
            variant="contained"
            onClick={() => setIsApplication(true)}
            sx={{
              bgcolor: tokens.color.teal,
              color: tokens.color.white,
              fontWeight: 700,
              borderRadius: `${tokens.radius.pill}px`,
              textTransform: "none",
              py: 1,
              "&:hover": { bgcolor: tokens.color.tealDark },
            }}
          >
            Apply now
          </Button>
        ) : (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              bgcolor: tokens.color.successBg,
              borderRadius: `${tokens.radius.sm}px`,
              padding: "10px 14px",
            }}
          >
            <TurnedIn sx={{ fontSize: "1.1rem", color: tokens.color.success }} />
            <Typography
              sx={{
                fontFamily: tokens.font.body,
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: tokens.color.success,
              }}
            >
              Already submitted a proposal
            </Typography>
          </Stack>
        )}

        <Stack spacing={0.5}>
          <Typography
            sx={{ fontFamily: tokens.font.body, fontSize: "0.75rem", color: tokens.color.inkMuted }}
          >
            Send a proposal for{" "}
            <Box component="span" sx={{ fontWeight: 700, color: tokens.color.ink }}>
              {job?.connect_need}
            </Box>{" "}
            connects
          </Typography>
          <Typography
            sx={{ fontFamily: tokens.font.body, fontSize: "0.75rem", color: tokens.color.inkMuted }}
          >
            Available connects:{" "}
            <Box component="span" sx={{ fontWeight: 700, color: tokens.color.ink }}>
              {job?.connect_available?.[0]?.availableConect}
            </Box>
          </Typography>
        </Stack>
      </Stack>

      <DetailSection title="About the client">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            sx={{ fontFamily: tokens.font.body, fontSize: "0.75rem", color: tokens.color.inkMuted }}
          >
            {paymentVerified ? "Payment method verified" : "Payment method not verified"}
          </Typography>
          <HelpRounded
            sx={{
              fontSize: "0.9rem",
              color: paymentVerified ? tokens.color.success : tokens.color.inkFaint,
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            src={"//yeapbe.com/" + job?.poster_info?.user_pic?.[0]?.pic_url}
            alt={job?.poster_info?.Name}
            sx={{ width: 36, height: 36 }}
          />
          <Typography
            sx={{ fontFamily: tokens.font.body, fontSize: "0.9rem", fontWeight: 700, color: tokens.color.ink }}
          >
            {job?.poster_info?.Name}
          </Typography>
          <CheckCircle
            sx={{
              fontSize: "0.85rem",
              color: posterVerified ? tokens.color.success : tokens.color.inkFaint,
            }}
          />
        </Stack>

        <Stack spacing={0}>
          <Typography
            sx={{ fontFamily: tokens.font.body, fontSize: "0.85rem", fontWeight: 700, color: tokens.color.ink }}
          >
            {job?.poster_info?.country}
          </Typography>
          <Typography
            sx={{ fontFamily: tokens.font.body, fontSize: "0.75rem", color: tokens.color.inkMuted }}
          >
            {job?.poster_info?.Location} {job?.post_time?.split(" ")[1]}
          </Typography>
        </Stack>

        <Stack spacing={0.5}>
          <Typography
            sx={{ fontFamily: tokens.font.body, fontSize: "0.85rem", fontWeight: 700, color: tokens.color.ink }}
          >
            {job?.poster_total_job} job posted
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Rating
              name="read-only"
              value={parseFloat(job?.user_rate?.average_rating || 0)}
              readOnly
              size="small"
              sx={{ color: tokens.color.teal }}
            />
            <Typography
              sx={{ fontFamily: tokens.font.body, fontSize: "0.8rem", color: tokens.color.inkMuted }}
            >
              ({job?.user_rate?.total_review || 0} reviews)
            </Typography>
          </Stack>
        </Stack>
      </DetailSection>

      <DetailSection noBorder>
        <Typography
          sx={{ fontFamily: tokens.font.body, fontSize: "0.8rem", fontWeight: 700, color: tokens.color.ink }}
        >
          Job link
        </Typography>
        <Button
          onClick={handleOpen}
          sx={{
            alignSelf: "flex-start",
            bgcolor: tokens.color.teal,
            color: tokens.color.white,
            fontSize: "0.75rem",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: `${tokens.radius.pill}px`,
            px: 2,
            "&:hover": { bgcolor: tokens.color.tealDark },
          }}
        >
          Share this job
        </Button>

        <Typography
          onClick={handleNavigation}
          sx={{
            mt: 1,
            textAlign: "center",
            cursor: "pointer",
            fontFamily: tokens.font.body,
            fontSize: "0.8125rem",
            fontWeight: 700,
            color: tokens.color.teal,
            "&:hover": { color: tokens.color.tealDark },
          }}
        >
          See more jobs from this agency
        </Typography>
      </DetailSection>
    </Box>
  );
};

export default JobSidebar;